import { EMarginMode, EProfitMode } from '@webtrader/core/api/market/symbols/detail';
import { ETradeFillPolicy } from '@webtrader/core/api/trade';
import {
  EOrderExportColumn,
  EOrderExportDealHistoriesColumn,
  EOrderExportHistoriesColumn,
  EOrderExportPendingColumn
} from '@webtrader/core/constants/order';
import request from '@webtrader/core/request';

export enum EOrderType {
  BUY,
  SELL,
  BUY_LIMIT,
  SELL_LIMIT,
  BUY_STOP,
  SELL_STOP,
  BALANCE,
  CREDIT,
  BUY_STOP_LIMIT,
  SELL_STOP_LIMIT,
  CLOSE_BY
}

export enum EOrderWebsocketMessageType {
  ADD_ORDER,
  CLOSE_ORDER,
  UPDATE_ORDER
}

export enum EOrderReason {
  CLIENT,
  EXPERT,
  DEALER,
  SIGNAL,
  GATEWAY,
  MOBILE,
  WEB,
  API,
  SL,
  TP,
  SO,
  ROLLOVER,
  EXTERNAL_CLIENT,
  VMARGIN,
  SETTLEMENT,
  TRANSFER,
  SYNC,
  EXTERNAL_SERVICE,
  MIGRATION,
  SPLIT
}

export enum EOrderState {
  OPEN_NORMAL,
  OPEN_REMAND,
  OPEN_RESTORED,
  CLOSED_NORMAL,
  CLOSED_PART,
  CLOSED_BY,
  DELETED,
  STARTED,
  PLACED,
  CANCELED,
  PARTIAL,
  FILLED,
  REJECTED,
  EXPIRED,
  REQUEST_ADD,
  REQUEST_MODIFY,
  REQUEST_CANCEL
}

export enum EOrderStateCentroid {
  CREATED,
  NEW,
  FILLED,
  PARTIALLY_FILLED,
  REJECTED,
  CANCELED
}

export enum EOrderStatus {
  CLOSED = 'CLOSED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
  CREDITED = 'CREDITED'
}

export enum EOrderIndicate {
  POINTS = 'POINTS',
  PRICE = 'PRICE'
}

export enum EOrderExpiry {
  GTC = 'GTC',
  GTD = 'GTD',
  SPECIFIED_TIMESTAMP = 'SPECIFIED_TIMESTAMP',
  SPECIFIED_DAY = 'SPECIFIED_DAY'
}

export enum EOrderBalanceType {
  SUBSCRIPTION,
  TOP_UP,
  WITHDRAWAL,
  UPFRONT_FEE,
  PERFORMANCE_FEE,
  UNSUBSCRIPTION,
  MANAGEMENT_FEE,
  PENALTY_FEE,
  DEPOSIT,
  OTHERS
}

export enum EOrderDealType {
  BUY,
  SELL,
  BALANCE,
  CREDIT,
  CHARGE,
  CORRECTION,
  BONUS,
  COMMISSION,
  COMMISSION_DAILY,
  COMMISSION_MONTHLY,
  AGENT_DAILY,
  AGENT_MONTHLY,
  INTERESTRATE,
  BUY_CANCELED,
  SELL_CANCELED,
  DIVIDEND,
  DIVIDEND_FRANKED,
  TAX,
  AGENT,
  SO_COMPENSATION,
  SO_COMPENSATION_CREDIT
}

export enum EOrderDealDirection {
  IN,
  OUT,
  INOUT,
  OUT_BY
}

export enum EOrderExecutionMode {
  EXE_REQUEST,
  EXE_INSTANT,
  EXE_MARKET,
  EXE_EXCHANGE
}

export type TOrderFillPolicy =
  | ETradeFillPolicy.FOK
  | ETradeFillPolicy.IOC
  | ETradeFillPolicy.RETURN
  | ETradeFillPolicy.GTC;

export enum EOrderHistoryType {
  ORDER = 'ORDER',
  DEAL = 'DEAL',
  POSITION = 'POSITION'
}

export interface IHistoryDateRange {
  to: Date;
  from: Date;
}

export interface IOrder {
  orderType: EOrderType;
  openPrice: number;
  closePrice: number;
  currentPrice: number;
  profit: number;
  orderId: string;
  reason: EOrderReason;
  stopLoss: number;
  state: EOrderState;
  symbol: string;
  takeProfit: number;
  volume: number;
  lotSize: number;
  decimal: number;
  contractSize: number;
  exchangeRate: number;
  expiration: number;
  openTime: number;
  closeTime: number;
  currency: string;
  tradeExpiry: EOrderExpiry;
  commission: number;
  swap: number;
  minContractLotSize: number;
  lotSizeStep: number;
  isEnable: boolean;
  units: number;
  indicate: EOrderIndicate;
  exeMode: EOrderExecutionMode;
  marketFillPolicy: ETradeFillPolicy[];
  fillPolicy: ETradeFillPolicy;
  editable?: boolean;
  trackable?: boolean;
  closable?: boolean;
  tradable: boolean;
  exitMethod: string;
  watch: boolean;
  profitMode: EProfitMode;
  tickSize: number;
  tickValue: number;
  marginMode: EMarginMode;
  marginPercentage: number;
  pointStep: number;
  // centroid
  notional: number;
}

export interface IOrderHistory
  extends Pick<
    IOrder,
    | 'orderType'
    | 'openPrice'
    | 'closePrice'
    | 'profit'
    | 'orderId'
    | 'reason'
    | 'stopLoss'
    | 'state'
    | 'symbol'
    | 'takeProfit'
    | 'volume'
    | 'lotSize'
    | 'decimal'
    | 'contractSize'
    | 'exchangeRate'
    | 'closeTime'
    | 'expiration'
    | 'openTime'
    | 'currency'
    | 'commission'
    | 'swap'
    | 'isEnable'
    | 'units'
    | 'exitMethod'
    | 'notional'
  > {
  profitTermCurrency: number;
  status: EOrderStatus;
  credit: number;
  deposit: number;
  withdrawal: number;
  realizedProfitLoss: number;
  comment: string;
  orderBalanceType: EOrderBalanceType;
  dealId: number;
  positionId: number;
  fee: number;
  dealType: EOrderDealType;
  dealDirection: EOrderDealDirection;
  totalLotSize: number;
  filledLotSize: number;
  remainedLotSize: number;
  avgFilledPrice: number;
  centroidOrderState: EOrderStateCentroid;
  centroidDeviation: number;
  fillPolicy: ETradeFillPolicy;
  openCenOrder: number;
  closeCenOrder: number;
  closedTransactionType: string; // similar to dealType (Centroid only)
}

export interface IGetOrderDetailsParams {
  orderId: number;
}

interface IOrderDateRangeEpoch {
  to: number;
  from: number;
}

export interface IGetOrderHistoriesParams extends IOrderDateRangeEpoch {
  type: EOrderHistoryType;
}

export interface IGetOrderCountsParam {
  symbol?: string;
}

export interface IGetOrderCountsResponse {
  marketOrderCounts: number;
  pendingOrderCounts: number;
}

export interface IOrderExport {
  ascending?: boolean;
  timeZoneId?: string;
}

export interface IGetOrderExportParams extends IOrderExport {
  symbol?: string;
  sortingColumn?: EOrderExportColumn;
  hiddenColumns?: EOrderExportColumn[];
}

export interface IGetOrderExportPendingParams extends IOrderExport {
  symbol?: string;
  sortingColumn?: EOrderExportPendingColumn;
  hiddenColumns?: EOrderExportPendingColumn[];
}

export interface IGetOrderExportDealHistoriesParams extends IOrderExport, IOrderDateRangeEpoch {
  sortingColumn?: EOrderExportDealHistoriesColumn;
  hiddenColumns?: EOrderExportDealHistoriesColumn[];
}

export interface IGetOrderExportHistoriesParams extends IOrderExport, IGetOrderHistoriesParams {
  sortingColumn?: EOrderExportHistoriesColumn;
  hiddenColumns?: EOrderExportHistoriesColumn[];
  symbol?: string;
}

export default {
  getAll: (params?: { symbol?: string }, signal?: AbortSignal) =>
    request.get<IOrder[]>('/api/order/v2', { params, signal }),
  getDetails: (params: IGetOrderDetailsParams) => request.get<IOrder>('/api/order/v2/details', { params }),
  getHistories: (params: IGetOrderHistoriesParams, signal?: AbortSignal) =>
    request.get<IOrderHistory[]>('/api/order/v3/histories', {
      params,
      signal
    }),
  getOrderCounts: (params: IGetOrderCountsParam, signal?: AbortSignal) =>
    request.get<IGetOrderCountsResponse>('/api/order/v1/counts', { params, signal }),
  // Export list of open positions to CSV
  export: (params?: IGetOrderExportParams) =>
    request.get('/api/trade/order/export/v1', { params, responseType: 'blob' }),
  // Export list of order & deal to CSV
  exportDealHistories: (params: IGetOrderExportDealHistoriesParams) =>
    request.get('/api/trade/order/export/v1/deal/histories', { params, responseType: 'blob' }),
  // Export list of Order history to CSV
  exportHistories: (params?: IGetOrderExportHistoriesParams) =>
    request.get('/api/trade/order/export/v1/histories', { params, responseType: 'blob' }),
  // Export list of pending order to CSV
  exportPending: (params?: IGetOrderExportPendingParams) =>
    request.get('/api/trade/order/export/v1/pending', { params, responseType: 'blob' })
};
